import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProductViewModel } from './viewmodels/product-viewmodel';

// Components
import { MobileProductHeader } from './components/ProductInfo';
import QuickBuyProductInfo from './components/QuickBuyProductInfo';
import ProductLoader from './components/ProductLoader';
import ProductAlert from './components/ProductAlert';
import RelatedProducts from './components/RelatedProducts';
import ProductDetailsContent from './components/ProductDetailsContent';
import ProductImages from './components/ProductImages';

// Layout components
import Navbar from '../../../components/Navbar'; 
import Footer from '../../../components/Footer';
import CartDrawer from '../../../components/CartDrawer';
import { useProductPageTemplate } from './templates/useProductPageTemplate';

const ProductPage = () => {
  const { id } = useParams();
  const {
    product,
    detailsHtml,
    loading,
    error,
    selectedVariant,
    quantity,
    activeImage,
    cartMessage,
    variantTypes,
    selectedVariantValues,
    selectedPack,
    packVariantSelections,
    handleVariantChange,
    handleVariantValueChange,
    handleQuantityChange,
    handleImageChange,
    handleAddToCart,
    handlePackSelect,
    handlePackVariantSelect,
    closeCartMessage,
    relatedProducts,
    relatedProductsLoading
  } = useProductViewModel(id);
  const templateConfig = useProductPageTemplate();
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    if (product) {
      document.title = `${product.name} | Votre E-commerce`;
    } else {
      document.title = 'Détails du produit | Votre E-commerce';
    }
  }, [product]);
  
  // Gallery: the selected variant's images first, then the product's (without duplicates).
  const images = product
    ? [...new Set([...(selectedVariant?.imageUrl || []), ...(product.imageUrl || [])].filter(Boolean))]
    : [];
  const gallery = (
    <ProductImages
      images={images}
      activeImage={activeImage}
      onImageChange={handleImageChange}
      alt={product?.name}
    />
  );

  if (error) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <div className="flex-grow pt-32 pb-16 px-4">
          <div className="container mx-auto text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="mt-4 text-lg font-medium text-gray-900">Une erreur est survenue</h2>
            <p className="mt-2 text-gray-500">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#4983a9] hover:bg-[#171340]"
            >
              Réessayer
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className={`flex flex-col min-h-screen overflow-x-hidden ${templateConfig.pageClass}`} style={templateConfig.styles || {}}>
      <Navbar />
      <main className={`flex-grow ${templateConfig.mainPadding}`}>
        <div className={`container mx-auto px-4 ${templateConfig.containerClass}`}>
          {loading ? (
            <ProductLoader />
          ) : (
            <>
              {/* Mobile Product Header - Shows only on mobile above images */}
              <MobileProductHeader 
                product={product} 
                selectedVariant={selectedVariant} 
              />

              <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
                {/* Buy-now panel: price, lead form, order summary */}
                <QuickBuyProductInfo
                  product={product}
                  selectedVariant={selectedVariant}
                  quantity={quantity}
                  onQuantityChange={handleQuantityChange}
                  onVariantChange={handleVariantChange}
                  onAddToCart={handleAddToCart}
                  variantTypes={variantTypes}
                  selectedPack={selectedPack}
                  onPackSelect={handlePackSelect}
                  gallery={gallery}
                />

                {/* Product gallery — desktop only; on mobile the same gallery renders inside
                    QuickBuyProductInfo, right under the price. */}
                <div className="hidden lg:block">{gallery}</div>
              </div>

              {/* Rich product content (images, video, formatted text), loaded after the page */}
              <ProductDetailsContent html={detailsHtml} />

              {/* Cart Message Alert */}
              <ProductAlert 
                message={cartMessage.message} 
                type={cartMessage.type} 
                show={cartMessage.show}
                onClose={closeCartMessage}
              />

              {/* Related Products - Same Category */}
              <RelatedProducts 
                products={relatedProducts}
                loading={relatedProductsLoading}
              />
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductPage;